import {
    Dialog, DialogContent, DialogTitle, LinearProgress, Typography,
} from '@mui/material';
import { Compare } from '@mui/icons-material';
import { useRecoilValue } from 'recoil';
import { useState } from 'react';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from 'src/contexts/ToastContext';
import { ButtonGroupWithPopper, ButtonGroupOptions } from '../ButtonGroupWithPopper';
import { buildCsvFromResults } from './utils/buildCsvFromResults';
import { runComparisons } from './utils/runComparisons';

export type PropTypes = {
    onSuccess?: () => void;
};

export const CompareTestsButton = (props: PropTypes): JSX.Element => {
    const { onSuccess } = props;
    const accessToken = useRecoilValue(isAuthorizedQuery);
    const { showToast } = useToast();
    const [currentOptions, setCurrentOptions] = useState<ButtonGroupOptions>({
        searchBackend: 'prod',
        experimentFlags: {},
    });
    const queryClient = useQueryClient();
    const [openModal, setOpenModal] = useState(false);
    const [progress, setProgress] = useState(0);

    const handleCompare = async (options: ButtonGroupOptions): Promise<void> => {
        try {
            setOpenModal(true);
            setProgress(0);

            const allResults = await runComparisons(
                accessToken,
                queryClient,
                options,
                setProgress,
                (msg) => showToast(msg, 'error'),
            );

            if (allResults.length === 0) {
                showToast('Comparison failed. The CSV could not be generated.', 'error');
                return;
            }

            const blob = buildCsvFromResults(allResults);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');

            a.href = url;
            a.download = `test_run_results_comparison_${new Date().toISOString()}.csv`;
            a.click();
            window.URL.revokeObjectURL(url);
            showToast('Comparison completed successfully', 'success');
            onSuccess?.();
        } finally {
            setOpenModal(false);
        }
    };

    const getButtonText = (options: ButtonGroupOptions): JSX.Element => {
        const experimentFlagCount = Object.values(options.experimentFlags || {}).length;
        const isBackendChanged = options.searchBackend !== 'prod';

        return (
            <>
                Export Comparison
                {(isBackendChanged || experimentFlagCount > 0) && (
                    <Typography
                        fontStyle="normal"
                        sx={{
                            textTransform: 'none',
                            pl: 1,
                        }}
                        variant="caption"
                    >
                        (
                        {options.searchBackend}
                        {`${experimentFlagCount > 0 ? `, ${experimentFlagCount} ${experimentFlagCount > 1 ? 'flags' : 'flag'}` : ''}`}
                        )
                    </Typography>
                )}
            </>
        );
    };

    const getTooltipText = (options: ButtonGroupOptions): string => {
        const hasValidExperimentFlags = Object.keys(options.experimentFlags).length > 0;
        const isBackendChanged = options.searchBackend !== 'prod';
        const isDisabled = !hasValidExperimentFlags && !isBackendChanged;

        if (isDisabled) {
            return 'Change backend from prod or add experiment flags to enable comparison';
        }

        return `Run comparison tests between the existing search and the ${options.searchBackend} backend${hasValidExperimentFlags ? ' with experiment flags' : ''}. Report will download as a CSV file.`;
    };

    const getIsDisabled = (options: ButtonGroupOptions): boolean => {
        const hasValidExperimentFlags = Object.keys(options.experimentFlags).length > 0;
        const isBackendChanged = options.searchBackend !== 'prod';

        return !hasValidExperimentFlags && !isBackendChanged;
    };

    return (
        <>
            <ButtonGroupWithPopper
                buttonIcon={<Compare />}
                buttonText={getButtonText(currentOptions)}
                isDisabled={getIsDisabled(currentOptions)}
                tooltipText={getTooltipText(currentOptions)}
                onButtonClick={handleCompare}
                onOptionsChange={setCurrentOptions}
            />
            <Dialog
                fullWidth
                maxWidth="xs"
                open={openModal}
            >
                <DialogTitle>
                    Comparing Relevance Tests...
                </DialogTitle>

                <DialogContent sx={{ textAlign: 'center' }}>
                    <LinearProgress value={progress} variant="determinate" />

                    <Typography
                        color="text.secondary"
                        sx={{ mt: 1 }}
                        variant="body2"
                    >
                        {progress}
                        %
                    </Typography>
                </DialogContent>
            </Dialog>

        </>
    );
};
